// Project Analyzer framework (proposal §3.5, Phase E).
//
// A LanguageAnalyzer analyzes one file in isolation. Some resolution, however,
// is inherently cross-file: deciding *which* file a called function or
// referenced type actually lives in requires whole-project information (module
// resolution, path aliases, re-export chains). A ProjectAnalyzer fills that gap.
//
// It consumes the per-file IR already produced by the LanguageAnalyzer pass
// (the `calls` / `type_reference` edges whose `to` is still a bare name) and
// returns those edges with `to` rewritten to a cross-file symbolId
// (targetFile#symbolName) wherever resolution succeeds. Unresolvable edges
// (calls into node_modules, dynamic dispatch) are returned unchanged so the
// edge count is preserved and the pipeline can substitute wholesale.
//
// As with LanguageAnalyzer, this carries behaviour (analyzeProject), so it is a
// TypeScript interface rather than a Zod schema. Implementations live in
// built-in/.

import type { GraphEdge } from "../types.js";
import type { AnalyzerCapability, FileAnalysisResult } from "./types.js";

// ---------------------------------------------------------------------------
// ProjectAnalysisInput
// ---------------------------------------------------------------------------

export interface ProjectAnalysisInput {
  /** Absolute project root; files in `files` are keyed relative to it. */
  projectRoot: string;
  /**
   * Per-file IR keyed by project-relative path. The unresolved `calls` /
   * `type_reference` edges to resolve live on each result's `edges`.
   */
  files: Map<string, FileAnalysisResult>;
  /**
   * Project-relative paths of files whose content changed since the last
   * run. When provided, only these files are re-resolved; unchanged files
   * reuse their cached post-resolution edges. When absent (--no-cache or
   * first run), all files are resolved.
   */
  changedFiles?: Set<string>;
}

// ---------------------------------------------------------------------------
// ProjectAnalysisResult
// ---------------------------------------------------------------------------

export interface ProjectAnalysisResult {
  /**
   * The complete set of `calls` / `type_reference` edges for every file the
   * analyzer handled, with `to` rewritten to a cross-file symbolId where
   * resolvable. Because every handled edge is reproduced (resolved or not),
   * these fully supersede the per-file unresolved edges — the pipeline drops a
   * handled file's `calls` / `type_reference` edges and substitutes these.
   */
  resolvedEdges: GraphEdge[];
  /**
   * Project-relative paths the analyzer processed. Used by the pipeline to know
   * whose `calls` / `type_reference` edges `resolvedEdges` supersedes; a file
   * absent here keeps its original per-file edges untouched.
   */
  handledFiles: string[];
}

// ---------------------------------------------------------------------------
// ProjectAnalyzer
// ---------------------------------------------------------------------------

export interface ProjectAnalyzer {
  readonly language: string;
  readonly capabilities: AnalyzerCapability[];
  /**
   * File extensions this analyzer handles, e.g. [".ts", ".tsx"]. The pipeline
   * uses these to decide which files in `files` the analyzer owns.
   */
  readonly fileExtensions?: readonly string[];
  analyzeProject(input: ProjectAnalysisInput): Promise<ProjectAnalysisResult>;
}

// ---------------------------------------------------------------------------
// Pipeline merge helper
// ---------------------------------------------------------------------------

/** Owning file of an edge: the part of `from` before any `#symbol` suffix. */
function ownerFile(edge: GraphEdge): string {
  const hash = edge.from.indexOf("#");
  return hash >= 0 ? edge.from.slice(0, hash) : edge.from;
}

const SUPERSEDED_KINDS = new Set<GraphEdge["kind"]>([
  "calls",
  "type_reference",
]);

/**
 * Apply a ProjectAnalysisResult to the per-file IR: for every handled file,
 * drop its `calls` / `type_reference` edges and substitute the analyzer's
 * resolved edges (grouped by owning file). Files the analyzer did not handle —
 * and edges of other kinds (imports, contains, …) — are passed through
 * unchanged. The input results and their edges are never mutated.
 */
export function applyProjectResolution(
  results: FileAnalysisResult[],
  resolution: ProjectAnalysisResult,
): FileAnalysisResult[] {
  const handled = new Set(resolution.handledFiles);
  if (handled.size === 0) return results;

  const byFile = new Map<string, GraphEdge[]>();
  for (const edge of resolution.resolvedEdges) {
    const file = ownerFile(edge);
    const list = byFile.get(file);
    if (list) list.push(edge);
    else byFile.set(file, [edge]);
  }

  return results.map((result) => {
    if (!handled.has(result.filePath)) return result;
    const kept = result.edges.filter((e) => !SUPERSEDED_KINDS.has(e.kind));
    const replaced = byFile.get(result.filePath) ?? [];
    return { ...result, edges: [...kept, ...replaced] };
  });
}
